import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { getPropertyByName } from "@buildwithflux/models";
import { roundFloatError } from "@buildwithflux/shared";
import { pick, groupBy, omit, pickBy, difference, minBy, mapValues, sumBy, chain, pull, } from "lodash";
import { box2, vec2 } from "../common/geometry";
import { SchematicAutoPlacement } from "./SchematicAutoPlacement";
// List of part types that how lower regularity (eg ICs vs resistors), so we
// can choose to only group high-regularity categories.
const IRREGULAR_PART_TYPES = [
    "Antenna",
    "Connector",
    "Display Device",
    "DRAM",
    "Filter",
    "FPGA",
    "Integrated Circuit",
    "Interface",
    "Isolator",
    "Linear Regulator",
    "Mechanical",
    "Microcontroller",
    "Power Management",
    "Processor",
    "pSRAM",
    "Relay",
    "RF Transceiver",
    "Sensor",
    "Transformer",
];
/**
 * Schematic auto-placement algorithm that groups components by part type.
 * Organizes components in columns based on part type, with each part type
 * in its own column. Components of the same type are arranged vertically
 * with consistent spacing.
 */
export class SchematicAutoPlacementByPartType extends SchematicAutoPlacement {
    document;
    elementGroups;
    groupName;
    elementUidsToPlace;
    partTypeSpacing; // Spacing between part type columns
    verticalSpacing; // Spacing between components in the same column
    groupSpacing; // Spacing between groups of columns positioned as a whole
    elementUidsInGroup;
    elementUidsToIgnore;
    constructor(document, elementGroups, groupName, elementUidsToPlace, opts = {}) {
        super(document, elementUidsToPlace);
        this.document = document;
        this.elementGroups = elementGroups;
        this.groupName = groupName;
        this.elementUidsToPlace = elementUidsToPlace;
        this.elementUidsInGroup = this.elementGroups[this.groupName] ?? [];
        this.elementUidsToIgnore = new Set(opts.elementUidsToIgnore ?? []);
        // Generous spacing to allow for portals (with labels) between pins of side-by-side ICs,
        // which copilot uses to connect things.
        this.partTypeSpacing = opts.partTypeSpacing ?? 100;
        this.verticalSpacing = opts.verticalSpacing ?? 60;
        this.groupSpacing = opts.groupSpacing ?? 300;
    }
    /**
     * Place components in a grid using a deterministic table-based layout approach.
     * This method groups elements by part type, arranges each group in a column,
     * and positions the entire grid to avoid collisions with unselected components.
     */
    runLayout() {
        this.applyPlacements(this.calculateGridPlacements());
        this.applyPlacements(this.calculateNonOverlappingGroupPlacements());
        return this.getPlacements();
    }
    calculateGridPlacements() {
        const elementPlacements = {};
        // Filter out ignored elements when calculating collision avoidance.
        // Less critical here since this handles within-group placement; elementUidsToIgnore
        // is mainly concerned with cross-group collision handling.
        const avoidElementUids = difference(this.elementUidsInGroup, this.elementUidsToPlace).filter((uid) => !this.elementUidsToIgnore.has(uid));
        const avoidElementBounds = pick(this.elementBounds, avoidElementUids);
        const selectionBounds = this.multiElementBounds(this.elementUidsToPlace);
        if (!selectionBounds)
            throw new Error("No elements specified");
        const selectedByPartType = this.groupElementsByPartType(this.elementUidsToPlace);
        const avoidByPartType = this.groupElementsByPartType(avoidElementUids);
        const remainingColumnsToOrganise = {};
        for (const [partType, elementUids] of Object.entries(selectedByPartType)) {
            const placements = {};
            const partTypeElementBounds = pick(this.elementBounds, elementUids);
            const center = box2.center(this.multiElementBounds(elementUids) ?? box2.empty());
            // Elements of a given part type don't necessarily all have the same size, so account for heights individually.
            let totalHeight = sumBy(Object.values(partTypeElementBounds), (b) => b.max.y - b.min.y + this.verticalSpacing);
            if (elementUids.length > 0) {
                totalHeight -= this.verticalSpacing;
            }
            const startY = totalHeight / 2; // Center the column vertically
            let currentY = startY;
            for (const [elementUid, bounds] of Object.entries(partTypeElementBounds)) {
                const height = bounds.max.y - bounds.min.y;
                placements[elementUid] = {
                    x: center.x,
                    y: center.y + currentY - height / 2,
                };
                currentY -= height + this.verticalSpacing;
            }
            // Find existing columns nearby to align to, if possible
            const othersOfType = avoidByPartType[partType] ?? [];
            let otherColumns = groupBy(othersOfType, (uid) => roundFloatError(box2.center(this.elementBounds[uid]).x));
            // Only consider columns that we could align beneath without further overlaps
            otherColumns = pickBy(otherColumns, (uids) => {
                const otherColumnBounds = this.multiElementBounds(uids) ?? box2.empty();
                const newColumnBounds = Object.entries(partTypeElementBounds).map(([uid, b]) => box2.translate(b, vec2.sub(placements[uid], box2.center(b))));
                const newColumnOverallBounds = box2.unionBox(...newColumnBounds) ?? box2.empty();
                const newPosition = {
                    x: box2.center(otherColumnBounds).x,
                    y: otherColumnBounds.min.y - this.verticalSpacing,
                };
                const offset = vec2.sub(newPosition, {
                    x: box2.center(newColumnOverallBounds).x,
                    y: newColumnOverallBounds.max.y,
                });
                const candidates = newColumnBounds.map((b) => box2.translate(b, offset));
                // TODO PERF: O(m*n^2) hit-testing algo here!!
                const overlaps = candidates.some((c) => {
                    return Object.values(avoidElementBounds).some((u) => box2.intersect(c, u));
                });
                return !overlaps;
            });
            const otherSinglePartColumns = pickBy(otherColumns, (uids) => uids.length === 1);
            // Initially, only consider columns with 2+ items
            const otherMultiColumns = pickBy(otherColumns, (uids) => uids.length >= 2);
            const closestColumn = minBy(Object.keys(otherMultiColumns), (x) => {
                const otherColumn = otherMultiColumns[x];
                const otherColumnBounds = this.multiElementBounds(otherColumn) ?? box2.empty();
                return vec2.distance(center, box2.center(otherColumnBounds));
            });
            let newPosition;
            if (closestColumn) {
                const otherColumnBounds = this.multiElementBounds(otherMultiColumns[closestColumn]) ?? box2.empty();
                newPosition = { x: box2.center(otherColumnBounds).x, y: otherColumnBounds.min.y - this.verticalSpacing };
            }
            else if (Object.keys(otherSinglePartColumns).length === 1) {
                // If there are no other multi-component columns, see if there is a single component
                // of the same type we can use. Only consider a single other component -- if there are
                // multiple isolated components, then we don't really have any good reason to
                // consider them a column. (Note: in future, if we have information to tell which IC
                // the other component belongs to, we could+should restrict the search to components
                // of that IC)
                const otherUid = Object.values(otherSinglePartColumns)[0][0];
                const otherBounds = this.elementBounds[otherUid];
                newPosition = { x: box2.center(otherBounds).x, y: otherBounds.min.y - this.verticalSpacing };
            }
            if (newPosition) {
                // TODO some code duplication here with the `otherColumns` loop above :-(
                const newColumnBounds = Object.entries(partTypeElementBounds).map(([uid, b]) => box2.translate(b, vec2.sub(placements[uid], box2.center(b))));
                const newColumnOverallBounds = box2.unionBox(...newColumnBounds) ?? box2.empty();
                const offset = vec2.sub(newPosition, {
                    x: box2.center(newColumnOverallBounds).x,
                    y: newColumnOverallBounds.max.y,
                });
                for (const [uid, placement] of Object.entries(placements)) {
                    elementPlacements[uid] = vec2.add(placement, offset);
                }
            }
            else {
                // If nothing nearby to align to, add this column to a columns-to-arrange list,
                // and organise them in a table separately.
                remainingColumnsToOrganise[partType] = placements;
            }
        }
        // Rough-enough guess at the number of existing columns, to determine the even-odd
        // vertical staggering.
        let numColumns = new Set(avoidElementUids.map((uid) => roundFloatError(box2.center(this.elementBounds[uid]).x))).size;
        // Space the remaining columns evenly, and align vertically, in the next available column
        // position alongside the already-placed columns.
        // Note: we use even spaces between column sides, not between column centers. Otherwise
        // large components and small components will be unevenly spaced, even to the point of
        // very-large components overlapping
        const remainingPartTypes = Object.keys(remainingColumnsToOrganise);
        const remainingUids = Object.values(remainingColumnsToOrganise).flatMap((p) => Object.keys(p));
        const alreadyPlacedUids = difference(this.elementUidsInGroup, remainingUids);
        const alreadyPlacedBounds = this.multiElementBounds(alreadyPlacedUids);
        let xPos = alreadyPlacedBounds ? alreadyPlacedBounds.max.x + this.partTypeSpacing : selectionBounds.min.x;
        const yPos = alreadyPlacedBounds ? alreadyPlacedBounds.max.y : selectionBounds.max.y;
        for (const partType of remainingPartTypes) {
            const placements = remainingColumnsToOrganise[partType];
            const newBounds = mapValues(placements, (p, uid) => box2.translate(this.elementBounds[uid], vec2.sub(p, box2.center(this.elementBounds[uid]))));
            const columnBounds = box2.unionBox(...Object.values(newBounds)) ?? box2.empty();
            const columnSize = box2.size(columnBounds);
            const columnXPos = xPos + columnSize.x / 2;
            const verticalStagger = numColumns % 2 ? 0 : this.verticalSpacing / 2;
            for (const [uid, placement] of Object.entries(placements)) {
                elementPlacements[uid] = {
                    x: columnXPos,
                    // We don't center vertically, because it wouldn't be resilient to adding new items later,
                    // and also because we want to stagger columns vertically to avoid label collisions.
                    y: yPos + (placement.y - columnBounds.max.y) + verticalStagger,
                };
            }
            xPos += columnSize.x + this.partTypeSpacing;
            numColumns++;
        }
        return elementPlacements;
    }
    findBestGroupPlacement() {
        const allGroupBounds = chain(this.elementGroups)
            .mapValues((uids) => this.multiElementBounds(uids.filter((uid) => !this.elementUidsToIgnore.has(uid))))
            .pickBy((v) => !!v)
            .value();
        const rows = [];
        const otherGroups = Object.keys(this.elementGroups).filter((g) => g !== this.groupName);
        let rowRepresentativeGroup;
        while ((rowRepresentativeGroup = otherGroups.pop())) {
            const groupBounds = allGroupBounds[rowRepresentativeGroup];
            if (!groupBounds)
                continue;
            // Groups share a row if they overlap vertically:
            const row = otherGroups.filter((g) => {
                const otherGroupBounds = allGroupBounds[g];
                if (!otherGroupBounds)
                    return;
                return groupBounds.min.y <= otherGroupBounds.max.y && groupBounds.max.y >= otherGroupBounds.min.y;
            });
            pull(otherGroups, ...row);
            row.push(rowRepresentativeGroup);
            const rowBounds = box2.unionBox(...row.map((g) => allGroupBounds[g]));
            if (!rowBounds)
                continue;
            rows.push(rowBounds);
        }
        const totalBounds = this.multiElementBounds(Object.keys(omit(this.elementBounds, this.elementUidsInGroup)).filter((uid) => !this.elementUidsToIgnore.has(uid))) ?? box2.empty();
        const groupBounds = this.multiElementBounds(this.elementUidsInGroup) ?? box2.empty();
        let best;
        rows.sort((a, b) => b.max.y - a.max.y);
        // Just use a fixed big-enough-but-not-too-big gap for now. Attempts to use a dynamic gap
        // based on row height didn't work very well -- it just ended up leaving way too much space
        // between rows, and fundamentally doesn't solve the problem of future components
        // potentially overlapping (which we'll have to address with a better algorithm in future)
        const rowGap = this.groupSpacing / 3;
        // Add an empty row at the bottom as one of the test rows. The group is added to the
        // empty row if it is better than an existing row.
        const emptyRow = box2.fromPoints([{ x: totalBounds.min.x, y: totalBounds.min.y - rowGap }]);
        const elementsInGroup = Object.values(pick(this.elementBounds, this.elementUidsInGroup));
        const otherElements = Object.values(omit(this.elementBounds, this.elementUidsInGroup));
        for (const row of [...rows, emptyRow]) {
            const pos = vec2.add(row.max, { x: box2.size(row).x ? this.groupSpacing : 0, y: 0 });
            const moveBy = vec2.sub(pos, { x: groupBounds.min.x, y: groupBounds.max.y });
            const elementsOverlap = elementsInGroup.some((e) => otherElements.some((o) => 
            // Leave space, esp. for zero-height elements with only 2 terminals (per comment below)
            box2.chain(e).expandByScalar(this.verticalSpacing).translate(moveBy).intersect(o)));
            if (elementsOverlap) {
                // We can't assume that this group won't overlap other elements, if its bounds are taller
                // than the candidate row. If any of the elements overlap here, find another row.
                continue;
            }
            const newTotalSize = box2.chain(groupBounds).translate(moveBy).unionBox(totalBounds).size();
            const unsquareness = Math.abs(Math.log2(newTotalSize.x / newTotalSize.y));
            if (!best || unsquareness < best.unsquareness) {
                best = { pos, unsquareness };
            }
        }
        return best?.pos ?? { x: 0, y: 0 };
    }
    /** Return the combined bounds of the given elements */
    multiElementBounds(elementUids) {
        return box2.unionBox(...Object.values(pick(this.elementBounds, elementUids)));
    }
    calculateNonOverlappingGroupPlacements() {
        const elementsInGroup = Object.values(pick(this.elementBounds, this.elementUidsInGroup));
        const otherElementUids = Object.keys(this.elementBounds).filter((uid) => !this.elementUidsInGroup.includes(uid) && !this.elementUidsToIgnore.has(uid));
        const otherElements = Object.values(pick(this.elementBounds, otherElementUids));
        const groupBounds = this.multiElementBounds(this.elementUidsInGroup);
        if (!groupBounds)
            throw new Error("No elements specified in group");
        const elementsOverlap = elementsInGroup.some((e) => otherElements.some((o) => box2.intersect(
        // Often bounding-boxes are zero-height because without svg assets accessible we
        // base the bounds on terminals, and many eg passives have just two terminals. So
        // this expansion ensures some intersection as well as leaving a gap for most parts.
        // Use `verticalSpacing` as the inter-part spacing because it's close enough to
        // what it means.
        box2.expandByScalar(e, this.verticalSpacing), o)));
        // Only (re-)position the group if all elements are newly added, or if some individual
        // elements overlap with elements outside the group. We don't check the entire group/other
        // bounding box for overlap, because that would cause us to move groups too eagerly when not
        // needed. We want to move as little as possible.
        const groupHasExistingElements = this.elementUidsInGroup.length > this.elementUidsToPlace.length;
        if (groupHasExistingElements && !elementsOverlap) {
            return {};
        }
        // Don't attempt to move groups with elements that have wires -- we don't have a reliable
        // rewire or keep-wires-intact mechanism right now.
        if (this.elementUidsInGroup.some((uid) => this.elementUidsWithWires.has(uid))) {
            return {};
        }
        const pos = this.findBestGroupPlacement();
        const offset = vec2.sub(pos, { x: groupBounds.min.x, y: groupBounds.max.y });
        const elementPlacements = {};
        for (const elementUid of this.elementUidsInGroup) {
            elementPlacements[elementUid] = vec2.add(this.elementPlacements[elementUid], offset);
        }
        return elementPlacements;
    }
    groupElementsByPartType(uids) {
        return groupBy(uids, (uid) => {
            const element = this.document.elements[uid];
            if (!element)
                return "Unknown";
            const partType = getPropertyByName(element, defaultPropertyDefinitions.part_type.label)
                ?.value;
            const isIrregularPartType = IRREGULAR_PART_TYPES.includes(partType);
            return (isIrregularPartType ? element.part_version_data_cache?.name : partType) || "Unknown";
        });
    }
}
//# sourceMappingURL=SchematicAutoPlacementByPartType.js.map